import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth0Data } from '../../context/Auth0DataContext';
import { useChatContext } from '../../context/ChatContext';
import ReactMarkdown from 'react-markdown';
import ConversationSidebar from './ConversationSidebar';

const { FiMessageCircle, FiSend, FiX, FiMinimize2, FiMaximize2, FiUser, FiBot, FiLoader, FiMenu, FiPlus } = FiIcons;

const ChatWidget = () => {
  const { user } = useAuth0Data();
  const {
    messages,
    sendMessage,
    isLoading,
    isMinimized,
    setIsMinimized,
    showChat,
    setShowChat,
    showSidebar,
    setShowSidebar,
    startNewConversation,
    activeConversationId,
    config,
  } = useChatContext();

  const [inputMessage, setInputMessage] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll when streaming content updates
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.isStreaming) {
      scrollToBottom();
    }
  }, [messages]);

  // Handle escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const message = inputMessage.trim();
    setInputMessage('');

    try {
      await sendMessage(message);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  if (!showChat) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setShowChat(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-50"
      >
        <SafeIcon icon={FiMessageCircle} className="w-6 h-6" />
      </motion.button>
    );
  }

  // Determine container classes based on state
  const getContainerClasses = () => {
    if (isFullscreen) {
      return 'fixed inset-0 rounded-none';
    }
    if (isMinimized) {
      return 'fixed bottom-6 right-6 w-96 h-16 rounded-xl';
    }
    const width = showSidebar ? 'w-[640px]' : 'w-96';
    return `fixed bottom-6 right-6 ${width} h-[32rem] rounded-xl`;
  };

  // Sidebar width in fullscreen vs normal
  const sidebarWidth = isFullscreen ? 280 : 240;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.9 }}
      className={`${getContainerClasses()} bg-white shadow-2xl border border-gray-200 z-50 transition-all duration-300 overflow-hidden`}
    >
      <div className="flex h-full">
        {/* Sidebar */}
        <AnimatePresence>
          {showSidebar && !isMinimized && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: sidebarWidth, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="h-full overflow-hidden flex-shrink-0"
            >
              <ConversationSidebar onClose={() => setShowSidebar(false)} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-primary-100 ${isFullscreen ? '' : 'rounded-tr-xl'}`}>
            <div className="flex items-center">
              {!isMinimized && (
                <button
                  onClick={() => setShowSidebar(!showSidebar)}
                  className="p-1.5 mr-2 text-gray-500 hover:text-gray-700 hover:bg-primary-200/50 rounded transition-colors"
                  title={showSidebar ? 'Hide conversations' : 'Show conversations'}
                >
                  <SafeIcon icon={FiMenu} className="w-4 h-4" />
                </button>
              )}
              <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiBot} className="w-4 h-4 text-white" />
              </div>
              <div className="ml-3">
                <h3 className="font-semibold text-gray-900">FuelDash AI</h3>
                {!isMinimized && (
                  <p className="text-xs text-gray-500">
                    Powered by Claude
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!isMinimized && (
                <>
                  <button
                    onClick={() => startNewConversation()}
                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-primary-200/50 rounded transition-colors"
                    title="New conversation"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-primary-200/50 rounded transition-colors"
                    title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
                  >
                    <SafeIcon icon={isFullscreen ? FiMinimize2 : FiMaximize2} className="w-4 h-4" />
                  </button>
                </>
              )}
              {!isFullscreen && (
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-primary-200/50 rounded transition-colors"
                  title={isMinimized ? 'Expand' : 'Collapse'}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isMinimized ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    )}
                  </svg>
                </button>
              )}
              <button
                onClick={() => {
                  setIsFullscreen(false);
                  setShowChat(false);
                }}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-primary-200/50 rounded transition-colors"
                title="Close"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div className={`flex-1 p-4 overflow-y-auto space-y-4 ${isFullscreen ? 'max-w-4xl mx-auto w-full' : ''}`}>
                {messages.length === 0 ? (
                  <div className={`text-center text-gray-500 ${isFullscreen ? 'mt-20' : 'mt-8'}`}>
                    <SafeIcon icon={FiMessageCircle} className={`mx-auto mb-4 opacity-50 ${isFullscreen ? 'w-16 h-16' : 'w-12 h-12'}`} />
                    <p className={`mb-2 ${isFullscreen ? 'text-lg' : ''}`}>Welcome to FuelDash AI!</p>
                    <p className={isFullscreen ? 'text-base' : 'text-sm'}>
                      Ask me about your revenue, team performance, projects, or uploaded documents.
                    </p>
                  </div>
                ) : (
                  <AnimatePresence>
                    {messages.map((message) => (
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`${isFullscreen ? 'max-w-[70%]' : 'max-w-[85%]'} p-3 rounded-lg ${
                            message.role === 'user'
                              ? 'bg-primary-500 text-white'
                              : message.isError
                              ? 'bg-red-50 text-red-900 border border-red-200'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {message.role === 'assistant' && (
                              <SafeIcon
                                icon={FiBot}
                                className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                                  message.isError ? 'text-red-500' : 'text-primary-500'
                                }`}
                              />
                            )}
                            {message.role === 'user' && (
                              <SafeIcon icon={FiUser} className="w-4 h-4 mt-0.5 text-primary-100 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              {message.role === 'assistant' ? (
                                <div className={`prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-2 prose-headings:font-semibold ${isFullscreen ? 'text-base' : 'text-sm'}`}>
                                  <ReactMarkdown
                                    components={{
                                      // Style markdown elements
                                      p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                                      ul: ({ children }) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
                                      ol: ({ children }) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
                                      li: ({ children }) => <li className="mb-1">{children}</li>,
                                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                      code: ({ inline, children }) =>
                                        inline
                                          ? <code className="bg-gray-200 px-1 rounded text-xs">{children}</code>
                                          : <code className="block bg-gray-200 p-2 rounded text-xs overflow-x-auto">{children}</code>,
                                      h1: ({ children }) => <h1 className="text-base font-bold mb-2">{children}</h1>,
                                      h2: ({ children }) => <h2 className="text-sm font-bold mb-2">{children}</h2>,
                                      h3: ({ children }) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
                                    }}
                                  >
                                    {message.content || (message.isStreaming ? '...' : '')}
                                  </ReactMarkdown>
                                  {message.isStreaming && (
                                    <span className="inline-block w-2 h-4 bg-primary-500 animate-pulse ml-1" />
                                  )}
                                </div>
                              ) : (
                                <p className={`whitespace-pre-wrap ${isFullscreen ? 'text-base' : 'text-sm'}`}>{message.content}</p>
                              )}
                              <p className="text-xs opacity-70 mt-1">
                                {new Date(message.timestamp).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}

                {/* Only show typing indicator when loading and no streaming message yet */}
                {isLoading && messages[messages.length - 1]?.role === 'user' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiBot} className="w-4 h-4 text-primary-500" />
                        <div className="flex items-center gap-1">
                          <SafeIcon icon={FiLoader} className="w-4 h-4 text-primary-500 animate-spin" />
                          <span className="text-sm text-gray-600">Thinking...</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className={`p-4 border-t border-gray-200 ${isFullscreen ? 'max-w-4xl mx-auto w-full' : ''}`}>
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me anything about your data..."
                    className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isFullscreen ? 'text-base py-3' : 'text-sm'}`}
                    disabled={isLoading}
                  />
                  <button
                    type="submit"
                    disabled={!inputMessage.trim() || isLoading}
                    className={`bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ${isFullscreen ? 'p-3' : 'p-2'}`}
                  >
                    <SafeIcon icon={FiSend} className={isFullscreen ? 'w-5 h-5' : 'w-4 h-4'} />
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ChatWidget;
